import React, { useEffect, useState } from 'react';
import { motion, useSpring, useScroll, useTransform, useInView } from 'framer-motion';
import { Sparkles, ArrowUpRight, Users, Trophy, Clock } from 'lucide-react';

const Hero = () => {
  const { scrollYProgress } = useScroll();
  
  const scaleProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const AnimatedValue = ({ value }: { value: string }) => {
    const ref = React.useRef(null);
    const isInView = useInView(ref, { 
      once: true,
      amount: 0.3,
      margin: "-50px"
    });
    
    const [number] = useState(() => {
      const num = parseInt(value);
      return isNaN(num) ? 0 : num;
    });

    const animatedValue = useSpring(0, {
      stiffness: 50,
      damping: 15,
      duration: 2000
    });

    useEffect(() => {
      if (isInView) {
        animatedValue.set(number);
      }
    }, [isInView, number, animatedValue]);

    return (
      <motion.span ref={ref} className="inline-block">
        {useTransform(animatedValue, latest => {
          const rounded = Math.round(latest);
          if (value.endsWith('+')) return `${rounded}+`;
          if (value.endsWith('%')) return `${rounded}%`;
          return `${rounded}`;
        })}
      </motion.span>
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    window.history.scrollRestoration = 'manual';
  }, []);

  return (
    <div className="relative min-h-screen bg-[#b3ebf2] overflow-hidden px-4 sm:px-6">
      {/* Animated background elements */}
      <motion.div className="absolute inset-0" style={{ y: useTransform(scaleProgress, [0, 1], ['0%', '10%']) }}>
        <motion.div 
          className="absolute -top-20 -right-20 w-80 sm:w-96 h-80 sm:h-96 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-40"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-20 -left-20 w-80 sm:w-96 h-80 sm:h-96 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-40"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
        <motion.div 
          className="absolute top-1/2 -translate-y-1/2 -left-40 w-96 h-96 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, -90, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear", delay: 5 }}
        />
        <motion.div 
          className="absolute top-1/2 -translate-y-1/2 -right-40 w-96 h-96 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [-90, 0, -90] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear", delay: 15 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto pt-20 sm:pt-32 pb-16">
        <div className="space-y-8 sm:space-y-12">
          {/* Floating badge */}
          <motion.div className="flex justify-center">
            <motion.div 
              className="bg-[#ffd54f]/10 rounded-full p-1 pr-4 sm:pr-6 flex items-center gap-2 border border-[#ffd54f]/20"
              whileHover={{ scale: 1.05, y: -2 }}
            >
              <motion.span 
                className="bg-[#ffd54f] rounded-full p-1"
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
              </motion.span>
              <span className="text-xs sm:text-sm text-[#f4b400] font-medium">We Help Your Brand Grow Online</span>
            </motion.div>
          </motion.div>

          {/* Main heading */}
          <div className="text-center">
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold tracking-tight">
              <motion.span 
                className="block text-gray-900"
                whileHover={{ scale: 1.02 }}
              >
                We Help Your Brand
              </motion.span>
              <motion.span 
                className="block text-[#f4b400] mt-2 sm:mt-3"
                whileHover={{ scale: 1.02 }}
              >
                Grow Online & Keep Growing
              </motion.span>
            </h1>
          </div>

          {/* Description */}
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base sm:text-xl text-gray-600">
              Transform your digital presence with cutting-edge marketing strategies that drive growth, 
              engage audiences, and deliver measurable results.
            </p>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
            <motion.button 
              className="w-full sm:w-auto group relative px-6 sm:px-8 py-3 sm:py-4 bg-[#ffd54f] text-white rounded-full text-base sm:text-lg font-medium overflow-hidden"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative flex items-center justify-center">
                Get Started
                <ArrowUpRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </span>
            </motion.button>
            
            <motion.button 
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-gray-900 border-2 border-[#81deea] bg-white text-base sm:text-lg font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Our Work
            </motion.button>
          </div>

          {/* Stats section */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-3xl mx-auto mt-12 sm:mt-20">
            {[
              { icon: Trophy, value: "500+", label: "Projects Delivered" },
              { icon: Users, value: "98%", label: "Client Satisfaction" },
              { icon: Clock, value: "24/7", label: "Support Available" }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center group"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: index * 0.2 }}
              >
                <motion.div 
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#ffd54f]/10 mb-4 group-hover:bg-[#ffd54f]/20"
                  whileHover={{ rotate: 360 }}
                >
                  <stat.icon className="w-6 h-6 text-[#f4b400]" />
                </motion.div>
                <div className="text-3xl sm:text-4xl font-bold text-[#f4b400]">
                  {stat.value === "24/7" ? (
                    stat.value
                  ) : (
                    <AnimatedValue value={stat.value} />
                  )}
                </div>
                <div className="text-sm sm:text-base text-gray-600 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;